import React from 'react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { API_PATH } from '../../config';
import { useMemo } from 'react';

import { useCan } from '../user/Can';
import { checkIsAdminOrSiteAdmin, Role } from '../user/Permission';
import { useUserKey } from '../user/ssoSlice';
import { UserApi } from '../user/userApi';
import { getStatusConfig } from '../../pages/vehicleModel/constant';
import { getBusTypeFromVin, resolveMeasurementStatus } from '../../pages/vehicleModel/VehicleModelHelper';

export const VehicleModelApi = createApi({
  reducerPath: "VehicleModelApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_PATH,
    prepareHeaders: (headers, { getState, endpoint }) => {
      const { user } = getState();
      headers.append('Authorization', `Token token="${user?.current?.auth?.key}"`)

      return headers
    },
  }),
  tagTypes: ["VehicleModel", "UserVoteHistory", 'Polls'],
  endpoints: (builder) => ({
    getVehicleModelCompatibilities: builder.query({
      query: (arg) => {
        let params = {
          company_id: arg.companyId,
          embed: arg.embed,
          pruning: "IDS"
        };

        if (arg.payload.q) {
          params = {...params, q:arg.payload.q}
        }

        const bodyPayload = arg.payload?.make && arg.payload?.model ?
          {
            make: arg.payload?.make,
            model: arg.payload?.model,
            year: arg.payload?.year,
          } : undefined;
          
        return {
          url: `/vehiclecompatibility`,
          method: bodyPayload ? 'POST' : 'GET',
          params,
          body : bodyPayload,   
        };
      },
      providesTags: ['VehicleModel'],
      async onQueryStarted({ companyId, userKey, embed, payload }, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;

        const getEndpointData = (endpoint, params = {}) => {
          const selector = VehicleModelApi.endpoints[endpoint].select(params);
          return selector(getState()).data;
        };

        const allKits = getEndpointData('getKits', { companyId });
        const allPids = getEndpointData('getPids', { companyId });
        const allDataPoolIdentifiers = getEndpointData('getDataPoolIdentifiers', { companyId });
        const allVins = getEndpointData('getVins', { companyId });
        const allVehicleModels = getEndpointData('getVehicleModels', { companyId });
        const allDeviceModels = getEndpointData('getDeviceModels');

        dispatch(
          VehicleModelApi.util.updateQueryData('getVehicleModelCompatibilities',
            { companyId, userKey, embed, payload }
            , (draft) => {
              const response = JSON.parse(JSON.stringify(draft));

              let index = 0;
              let formattedResponse = [];

              const createIdMap = (array) => array.reduce((map, item) => {
                map[item.id] = item;
                return map;
              }, {});

              const [kitDetailMap, vehicleModelMap, vinMap, deviceModelsMap, pidIndex] = [
                allKits,
                allVehicleModels,
                allVins,
                allDeviceModels,
                allPids
              ].map(createIdMap);

              const combinedDataPoolIdentifier = Object.values(allDataPoolIdentifiers).reduce((acc, inner) => {
                return { ...acc, ...inner };
              }, {});

              const reverseMap = new Map();

              Object.entries(allDataPoolIdentifiers).forEach(([outerKey, innerObj]) => {
                Object.keys(innerObj).forEach(innerKey => {
                  reverseMap.set(innerKey, outerKey);
                });
              });

              response.map((data) => {
                data.vehicleModel = { ...vehicleModelMap[data.id] };
                if (data.vehicleModel.extras) {
                  data.vehicleModel.extras = JSON.parse(data.vehicleModel.extras);
                }

                if (data.vehicleModel?.source === undefined && data.vehicleModel?.displaySource) {
                  data.vehicleModel.source = data.vehicleModel.displaySource;
                }

                const supportedKit = {};
                let supportedDeviceModel = new Set();
                if (data.deviceModels) {
                  data.supportedPid = {};
                  data.deviceModels.forEach((deviceModel) => {
                    const deviceModelObj = deviceModelsMap[deviceModel.id];
                    supportedDeviceModel.add(deviceModelObj);
                    supportedKit[deviceModelObj.name] = [];

                    if (deviceModel.availableKits && deviceModel.availableKits.length !== 0) {
                      deviceModel.availableKits.map((kit) => {
                        let kitDetail = kitDetailMap[kit.kitId];
                        supportedKit[deviceModelObj.name].push({
                          id: kit.kitId,
                          sapCode: kitDetail.sapCode,
                          deviceModel: deviceModelObj.name,
                          deviceModelId: deviceModelObj.id,
                          sapType: kitDetail.sapType,
                          kitType: kit.kitType
                        })
                      });
                    }
                  });
                }
                data.vehicleModel.pidVerificationStatus = data.pidVerificationStatus;
                data.vehicleModel.supportedPid = [];
                data.vehicleModel.vins = [];
                (data.vinIds || []).forEach(v => {
                  const vinDetail = vinMap[v];
                  const vinExtras = JSON.parse(vinDetail.extras || '{}');

                  // Use map for the inner loop to create an array of pidData objects
                  const modelPids = (vinDetail.pids || []).map(i=>JSON.parse(JSON.stringify(pidIndex[i] || '{}')));

                  const statusConfig = getStatusConfig();
                  const busType = getBusTypeFromVin(vinDetail.vin);

                  const pidDataArray = Object.keys(combinedDataPoolIdentifier).map(k => {
                    const pidData = modelPids.find(i=> (i.dataPoolIdentifiers || []).includes(k));
                    const supported = pidData !== undefined;

                    const dataKey = {key : k};
                    const { status, calculated } = resolveMeasurementStatus(dataKey, modelPids, busType, statusConfig, data.vehicleModel);

                    return {
                      description: combinedDataPoolIdentifier[k],
                      dbcKey: vinExtras.dbc,
                      vinKey: vinDetail.vin,
                      canId: vinExtras.can_id,
                      verificationStatus: data.pidVerificationStatus?.[pidData?.id] ?? status.status,
                      category: reverseMap.get(k),
                      calculated : calculated,
                      id: supported ? pidData.id : undefined,
                      dataPoolIdentifier: k,
                      busType: supported ? pidData.busType : "",
                      tn360SupportStatus: supported ? pidData.tn360SupportStatus : false,
                      pid: supported ? pidData.pid : "",
                    }
                  });
                  data.vehicleModel.supportedPid = [...data.vehicleModel.supportedPid, ...pidDataArray];
                  data.vehicleModel.vins.push({
                    vinDetail: {
                      ...vinDetail,
                      canId: vinExtras.can_id,
                      dbcKey: vinExtras.dbc,
                    },
                    modelPids: modelPids.map(pid => ({
                      ...pid,
                      verificationStatus: data.pidVerificationStatus?.[pid.id] })) });
                });

                data.vehicleModel.supportedKit = [];

                if (supportedDeviceModel.size !== 0) {
                  data.vehicleModel.supportedModels = [...supportedDeviceModel];
                  [...supportedDeviceModel].map(deviceModel => {
                    data.vehicleModel.supportedKit = [...new Map([...data.vehicleModel.supportedKit, ...Object.values(supportedKit[deviceModel.name])].map(item => [`${item.id}_${item.deviceModelId}`, item])).values()];
                  });

                  if (data.vehicleModel.supportedPid && data.vehicleModel.supportedPid.length !== 0) {
                    data.vehicleModel.supportedDataPoolIdentifier = data.vehicleModel.supportedPid.filter(i=>i.verificationStatus !== "N" && i.verificationStatus !== "X").map(i=>i.dataPoolIdentifier);
                    data.vehicleModel.grouppedPid = data.vehicleModel.supportedPid.filter(i=> i.pid !== "").reduce((acc, obj) => {
                      const category = obj.category.toUpperCase() === "UNKNOWN" ? "Others" : obj.category;
                      if (!acc[category]) {
                        acc[category] = [];
                      }
                      acc[category].push(obj.description);
                      return acc;
                    }, {});
                  }
                  
                  delete data.supportedPid;
                }
                else {
                  data.vehicleModel.supportedModels = [];
                  data.vehicleModel.supportedDataPoolIdentifier = [];
                }

                data.vehicleModel.pidStatus = JSON.stringify(data.vehicleModel.supportedPid.filter(i=> !!i.id).sort((a, b) => a.id - b.id).map(i=>i.id+i.verificationStatus));
                data.vehicleModel.rowId = ++index;
                data.vehicleModel.connectionPoint = JSON.stringify(data.vehicleModel.extras?.connectionPoint);
                data.vehicleModel.attachments = data.attachments;
                data.vehicleModel.attachmentIds = JSON.stringify((data.attachments || []).map(i=>i.attachmentId).sort((a, b) => a - b));

                if (data.vehicleModel?.extras?.hideHcv === undefined) {
                  formattedResponse.push(data.vehicleModel);
                }
                delete data.deviceModels;
              })

              return formattedResponse;
            })
        )
      }
    }),
    getUnverifiedVehicleModels: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = {
          company_id: companyId
        };

        return {
          url: `/vehiclemodels/unverifiedmake`,
          params
        };
      },
      providesTags: ['VehicleModel'],
      async onQueryStarted({ companyId }, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;
        const selectVehicleModels = VehicleModelApi.endpoints.getVehicleModels.select({ companyId });

        const allVehicleModels = selectVehicleModels(getState()).data;

        dispatch(
          VehicleModelApi.util.updateQueryData('getUnverifiedVehicleModels',
            { companyId }
            , (draft) => {
              const response = JSON.parse(JSON.stringify(draft));

              let vehicleModelMap = allVehicleModels.reduce((map, item) => {
                map[item.id] = item;
                return map;
              }, {});

              let formattedResponse = [];

              formattedResponse = response.filter(i => vehicleModelMap[i.id] !== undefined).map((data) => {
                return { ...data, ...vehicleModelMap[data.id] };
              });

              return formattedResponse;
            })
        )
      },
    }),
    bulkUpdateVehicleModel: builder.mutation({
      query: (payload) => {
        const { id, action } = payload;
        let url = '/vehiclemodels/' + id + '/make/' + action;

        return {
          url,
          method: 'PUT',
          body: payload,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      invalidatesTags: ['VehicleModel'],
    }),
    getVehicleModels: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = {
          company_id: companyId
        };

        return {
          url: `/vehiclemodels`,
          params
        };
      },
      providesTags: ['VehicleModel'],
      transformResponse: (response, meta, arg) => {
        let filteredReponse = [];
        response.forEach(data => {
          if (data.source === undefined && data.displaySource) {
            data.source = data.displaySource
          }

          //do filtering here
          if (data.make?.toLowerCase() === data.model?.toLowerCase() || 
              !isNaN(data.make) || data.make?.trim().length < 2) {
              return;
          }
          
          filteredReponse.push(data);
        })
        return filteredReponse;
      }
    }),
    getVehicleModelById: builder.query({
      query: (arg) => {
        const { id } = arg;
        let url = '/vehiclemodels';

        if (id) {
          url += "/" + id;
        }

        return {
          url
        };
      },
      providesTags: ['VehicleModel'],
      transformResponse: (response, meta, arg) => {
        if (response.source === undefined && response.displaySource) {
          response.source = response.displaySource
        }
        return response;
      }
    }),
    getVehicleModelDocumentById: builder.query({
      query: (arg) => {
        const { id, documentId } = arg;
        let url = `/eventattachments/documentDetails?vehicleModelId=${id}&attachmentId=${documentId}`;

        return {
          url
        };
      },
      providesTags: ['VehicleModel'],
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: documentData } = await queryFulfilled;

          const {currentUser, userKey, isAdminOrSiteAdmin, payload} = getVehicleCompatibilityQueryDetail(getState);

          dispatch(
            VehicleModelApi.util.updateQueryData('getVehicleModelCompatibilities',
              { companyId: currentUser?.companyId, userKey, embed: isAdminOrSiteAdmin ? "devicemodels,kits" : "devicemodels",payload }
              , (draft) => {
                const cacheData = draft.filter(i => i.id === id);

                (cacheData || []).forEach(c => {
                  c.documentInfo = documentData;
                  c.isDocumentFetched = true;
                  c.make = c.make;
                })
              })
          )
        }
        catch {

        }
      },
      invalidatesTags: ['VehicleModel']
    }),
    mergeVehicleModel: builder.mutation({
      query: (payload) => {
        let url = '/vehiclemodels/merge';

        return {
          url,
          method: 'PUT',
          body: payload,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      invalidatesTags: ['VehicleModel'],
    }),
    updateVehicleModelMeasurement: builder.mutation({
      query: (payload) => {
        let url = '/vehiclemodels/measurement';

        return {
          url,
          method: 'PUT',
          body: payload,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      async onQueryStarted({ vehicleModelIds, pids }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: updatedData } = await queryFulfilled;
          const {currentUser, userKey, isAdminOrSiteAdmin, payload} = getVehicleCompatibilityQueryDetail(getState);

          vehicleModelIds.forEach(vm=>{
            dispatch(
              VehicleModelApi.util.updateQueryData('getVehicleModelCompatibilities',
                { companyId: currentUser?.companyId, userKey, embed: isAdminOrSiteAdmin ? "devicemodels,kits" : "devicemodels", payload }
                , (draft) => {
                  const cacheData = draft.find(i => i.id === vm);
  
                  if (cacheData) {
                    cacheData.supportedPid = cacheData.supportedPid.map(i=>{
                      return {...i,
                        verificationStatus: pids[i.id] ?? (i.verificationStatus)
                      }
                    });
                    cacheData.vins = cacheData.vins.map(i=>{
                      return {
                        ...i,
                        modelPids: i.modelPids.map(p=>{
                          return {
                            ...p,
                            verificationStatus: pids[p.id] ?? ( p.verificationStatus)
                          }
                        })
                      }
                    });
                    cacheData.pidStatus = JSON.stringify(cacheData.supportedPid.sort((a, b) => a.id - b.id).map(i=>i.id+i.verificationStatus));
                  }
                })
            )
          })
        }
        catch {

        }
      }
    }),
    saveVehicleModel: builder.mutation({
      query: (payload) => {
        let url = '/vehiclemodels';

        if (payload.id) {
          url += "/" + payload.id;
        }

        return {
          url,
          method: payload.id ? 'PUT' : 'POST',
          body: payload,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: updatedData } = await queryFulfilled;
          const {currentUser, userKey, isAdminOrSiteAdmin, payload} = getVehicleCompatibilityQueryDetail(getState);

          dispatch(
            VehicleModelApi.util.updateQueryData('getVehicleModelCompatibilities',
              { companyId: currentUser?.companyId, userKey, embed: isAdminOrSiteAdmin ? "devicemodels,kits" : "devicemodels", payload }
              , (draft) => {
                const cacheData = draft.find(i => i.id === updatedData.id);

                if (cacheData) {
                  updateVehicleModel(cacheData, {...updatedData, extras: JSON.parse(updatedData.extras|| '{}')});
                }
                else {
                  return [...[updatedData], ...draft];
                }
              })
          )

          if (id) {
            dispatch(
              VehicleModelApi.util.updateQueryData('getVehicleModelById',
                { id: updatedData.id }
                , (draft) => {
                  updateVehicleModel(draft, updatedData);
                })
            )
          }

          dispatch(
            VehicleModelApi.util.updateQueryData('getVehicleModels',
              { companyId: currentUser?.companyId }
              , (draft) => {
                const cacheData = draft.find(i => i.id === updatedData.id);

                if (cacheData) {
                  updateVehicleModel(cacheData, updatedData);
                }
                else {
                  return [...[updatedData], ...draft];
                }
              })
          )
        }
        catch (e) {
          console.log(e)
        }
      },
    }),
    saveVehicleModelBulk: builder.mutation({
      query: (payload) => {
        let url = '/vehiclemodels/bulk';
        return {
          url,
          method: 'PUT',
          body: payload,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: updatedData } = await queryFulfilled;
          const {currentUser, userKey, isAdminOrSiteAdmin, payload} = getVehicleCompatibilityQueryDetail(getState);

          (updatedData.vehicleModels || []).forEach(e => {
            dispatch(
              VehicleModelApi.util.updateQueryData('getVehicleModelCompatibilities',
                { companyId: currentUser?.companyId, userKey, embed: isAdminOrSiteAdmin ? "devicemodels,kits" : "devicemodels", payload }
                , (draft) => {
                  const cacheData = draft.find(i => i.id === e.id);

                  if (cacheData) {
                    updateVehicleModel(cacheData, {...e, extras: JSON.parse(e.extras|| '{}')});
                  }
                  else {
                    return [...[e], ...draft];
                  }
                })
            );

            dispatch(
              VehicleModelApi.util.updateQueryData('getVehicleModelById',
                { id: e.id }
                , (draft) => {
                  updateVehicleModel(draft, e);
                })
            )

            dispatch(
              VehicleModelApi.util.updateQueryData('getVehicleModels',
                { companyId: currentUser?.companyId }
                , (draft) => {
                  const cacheData = draft.find(i => i.id === e.id);
  
                  if (cacheData) {
                    updateVehicleModel(cacheData, e);
                  }
                  else {
                    return [...[e], ...draft];
                  }
                })
            )
          })
        }
        catch {

        }
      },
    }),
    deleteVehicleModel: builder.mutation({
      query: (payload) => {
        let url = '/vehiclemodels';

        if (payload.id) {
          url += "/" + payload.id;
        }

        return {
          url,
          method: 'DELETE',
          body: payload,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        try {
          const {currentUser, userKey, isAdminOrSiteAdmin, payload} = getVehicleCompatibilityQueryDetail(getState);

          dispatch(
            VehicleModelApi.util.updateQueryData('getVehicleModelCompatibilities',
              { companyId: currentUser?.companyId, userKey, embed: isAdminOrSiteAdmin ? "devicemodels,kits" : "devicemodels", payload }
              , (draft) => {
                return draft.filter(i => i.id !== id);
              })
          )

          dispatch(
            VehicleModelApi.util.updateQueryData('getVehicleModels',
              { companyId: currentUser?.companyId }
              , (draft) => {
                return draft.filter(i => i.id !== id);
              })
          )
        }
        catch {

        }
      },
    }),
    getDeviceModels: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = companyId ? { company_id: companyId } : undefined;

        return {
          url: arg.compatibleModelOnly ? `/vehiclecompatibility/deviceModels` : `/devices/models`,
          params
        };
      },
      transformResponse: (response, meta, arg) => {
        const customNameMapper = {
          ["20Wire"] : "VT101",
          ["AdvGateway"] : "VT102",
          ["AdvRugged"] : "RE400",
          ["9Wire"] : "RE200",
          ["OBDII"] : "SI201"
        }
        
        return response.map(i=> {
          return {
            ...i,
            name : customNameMapper[i.name] ?? i.name
          }
        });
      }
    }),
    getPids: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = { company_id: companyId };

        return {
          url: `/vehiclecompatibility/pids?pruning=web`,
          params
        };
      }
    }),
    getDeviceTypes: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = { company_id: companyId };

        return {
          url: `/vehiclecompatibility/deviceTypes`,
          params
        };
      }
    }),
    getVins: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = { company_id: companyId };

        return {
          url: `/vehiclecompatibility/vins`,
          params
        };
      }
    }),
    getDataPoolIdentifiers: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = { company_id: companyId };

        return {
          url: `/vehiclecompatibility/datapoolIdentifiers`,
          params
        };
      }
    }),
    getKits: builder.query({
      query: (arg) => {
        const { companyId } = arg;
        const params = { company_id: companyId };

        return {
          url: `/vehiclecompatibility/kits`,
          params
        };
      }
    }),
    voteForScanRequest: builder.mutation({
      query: (payload) => {
        const { id } = payload;
        let url = '/vehiclemodels/' + id + '/vote';

        return {
          url,
          method: 'POST',
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      invalidatesTags: ['UserVoteHistory', 'Polls'],
    }),
    unVoteForScanRequest: builder.mutation({
      query: (payload) => {
        const { id } = payload;
        let url = '/vehiclemodels/' + id + '/unvote';

        return {
          url,
          method: 'POST',
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      invalidatesTags: ['UserVoteHistory', 'Polls'],
    }),
    changePollStatus: builder.mutation({
      query: (payload) => {
        const { id } = payload;
        let url = '/vehiclemodels/poll/' + id;

        return {
          url,
          method: 'PUT',
          body: payload,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      },
      invalidatesTags: ['UserVoteHistory', 'Polls'],
    }),
    getUserVoteHistory: builder.query({
      query: () => {
        return {
          url: `/vehiclemodels/poll/history`
        };
      },
      providesTags: ['UserVoteHistory']
    }),
    getPolls: builder.query({
      query: () => {
        return {
          url: `/vehiclemodels/polls`
        };
      },
      providesTags: ['Polls']
    })
  }),
})

const updateVehicleModel = (cacheData, updatedData) => {
  cacheData.make = updatedData.make;
  cacheData.model = updatedData.model;
  cacheData.yearOfManufacture = updatedData.yearOfManufacture;
  cacheData.isSupported = updatedData.isSupported;
  cacheData.priority = updatedData.priority;
  cacheData.dbcFileKey = updatedData.dbcFileKey;
  cacheData.extras = updatedData.extras;
}

export const {
  useGetVehicleModelCompatibilitiesQuery,
  useGetVehicleModelsQuery,
  useGetVehicleModelByIdQuery,
  useGetUnverifiedVehicleModelsQuery,
  useGetPidsQuery,
  useGetDeviceModelsQuery,
  useGetDeviceTypesQuery,
  useGetKitsQuery,
  useGetVinsQuery,
  useGetDataPoolIdentifiersQuery,
  useGetUserVoteHistoryQuery,
  useGetPollsQuery,
  useSaveVehicleModelMutation,
  useSaveVehicleModelBulkMutation,
  useDeleteVehicleModelMutation,
  useMergeVehicleModelMutation,
  useBulkUpdateVehicleModelMutation,
  useChangePollStatusMutation,
  useVoteForScanRequestMutation,
  useUnVoteForScanRequestMutation,
  useUpdateVehicleModelMeasurementMutation
} = VehicleModelApi

export const { getVehicleModelDocumentById } = VehicleModelApi.endpoints;

export const useUnverifiedVehicle = (companyId) => {
  const { data } = useGetVehicleModelsQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  const { data: vehicleModels, isFetching } = useGetUnverifiedVehicleModelsQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined || data === undefined });

  return useMemo(() => {
    const isCompatiblityDataLoaded = (vehicleModels || []).length !== 0 && vehicleModels[0].source !== undefined;

    if (isCompatiblityDataLoaded) {
      return {
        vehicleModels: vehicleModels,
        isFetching: isFetching
      };
    }
    else {
      return { isFetching: true };
    }
  }, [vehicleModels, isFetching]);
}

export const useVehicleModelWithSource = (companyId, source = 'internal') => {
  const { data, isFetching } = useGetVehicleModelsQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  return useMemo(() => {
    if (data) {
      const targetSource = source;
      return {
        vehicleModels: (data || []).filter(
          i =>
            targetSource === undefined ||
            i.source?.toLowerCase() === targetSource?.toLowerCase()
        ),
        isFetching: isFetching
      };
    }
    else {
      return { isFetching: true };
    }
  }, [data, isFetching]);
}

export const usePolls = () => {
  const { data: history, isFetching: isHistoryFetching } = useGetUserVoteHistoryQuery({}, { pollingInterval: 3600000 });
  const { data: polls, isFetching: isPollsFetching } = useGetPollsQuery({}, { pollingInterval: 3600000 });

  return useMemo(() => {

    if (!isPollsFetching && !isHistoryFetching && history && polls) {
      const clonedList = new Array();
      history.forEach(obj => {
        const matchedPoll = polls.filter(poll => obj.pollId === poll.id)[0];
        const cloned = {
          ...obj,
          vehicleModel: matchedPoll.vehicleModel,
          comment: matchedPoll.comment,
          status: matchedPoll.status
        };
        clonedList.push(cloned);
      });
      return {
        polls,
        history: clonedList,
        isFetching: false
      };
    }
    else {
      return { isFetching: true };
    }
  }, [history, isHistoryFetching, polls, isPollsFetching]);
}

export const useVehicleCompatibility = (companyId, forceSkip, payload) => {
  const can = useCan();
  const userKey = useUserKey();
  const { data: allKits } = useGetKitsQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  const { data: allPids } = useGetPidsQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  const { data: allVins } = useGetVinsQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  const { data: allDataPoolIdentifiers } = useGetDataPoolIdentifiersQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  const { data: allDeviceTypes } = useGetDeviceTypesQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  const { data: vehicleModels } = useGetVehicleModelsQuery({ companyId: companyId }, { pollingInterval: 3600000, skip: companyId === undefined });
  const { data: deviceModels } = useGetDeviceModelsQuery({}, { pollingInterval: 3600000 });

  const isFilteringDataFetched = allKits !== undefined && allDataPoolIdentifiers !== undefined &&
    allPids !== undefined && vehicleModels !== undefined && allDeviceTypes !== undefined && allVins !== undefined &&
    deviceModels !== undefined;

  const { data: compatibilityData, isFetching: isFetchingVehicleCompatibility } = useGetVehicleModelCompatibilitiesQuery({
    companyId: companyId, userKey,
    payload: { make: payload?.make, model: payload?.model, year: payload?.year, q: payload?.q }, embed: can({ oneOfRole: [Role.ADMIN, Role.RESELLER, Role.READONLY_ADMIN, Role.SITE_ADMIN] }) ? "devicemodels,kits" : "devicemodels"
  },
    {
      pollingInterval: 3600000,
      skip: forceSkip === true || companyId === undefined || !isFilteringDataFetched
    });

  return useMemo(() => {
    const isCompatiblityDataLoaded =!!compatibilityData && ((compatibilityData || []).length === 0 || compatibilityData[0].make !== undefined);

    if (isCompatiblityDataLoaded && !isFetchingVehicleCompatibility) {
      const filteredSupported = payload?.isSupported || [];
      const filteredMeasurements = payload?.measurements || [];
      let data = [...compatibilityData];

      if (filteredSupported.length !== 0) {
        data = data.filter(i => filteredSupported.includes(i.isSupported ?? -1));
      }

      if (filteredMeasurements.length !== 0) {
        data = data.filter(i => filteredMeasurements.every(item => (i.supportedDataPoolIdentifier || []).includes(item)));
      }
      
      return {
        vehicleCompatibility: data,
        allDeviceTypes: allDeviceTypes,
        allKits,
        vehicleModels,
        isFetching: isFetchingVehicleCompatibility || data === undefined
      };
    }
    else {
      return {
        isFetching: forceSkip ? !isFilteringDataFetched : true,
        allDeviceTypes: allDeviceTypes,
        allKits,
        vehicleModels
      };
    }
  }, [compatibilityData, allKits, allPids, allDeviceTypes, isFetchingVehicleCompatibility, isFilteringDataFetched, payload]);
};

export const useGrouppedVehicleCompatibility = (companyId, forceSkip, payload) => {
  const { vehicleCompatibility, isFetching } = useVehicleCompatibility(companyId, forceSkip, payload);

  return useMemo(() => {
    const groupedModelData = (vehicleCompatibility || []).reduce((acc, item) => {
      const key = `${(item.make +"_" + item.model+"_"+item.yearOfManufacture+ "_"+item.company?.id ?? "-1") + "_" + (item.isSupported || 'unknown')}`;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(item);

      return acc;
    }, {});

    const uniqueList = (vehicleCompatibility || []).reduce((acc, current) => {
      const currentGroupModel = groupedModelData[(current.make +"_" + current.model+"_"+current.yearOfManufacture + "_"+current.company?.id ?? "-1")+ "_" + (current.isSupported || 'unknown')];
      let matchedModelList = [];

      currentGroupModel.forEach(c=>{
        if (matchedModelList.length === 0) {
          matchedModelList = acc.filter(item => item.make === c.make &&
            item.model === c.model &&
            item.isSupported === c.isSupported &&
            ((item.source !== c.source && item.yearOfManufacture === c.yearOfManufacture) ||
              (item.attachmentIds === c.attachmentIds &&
                item.priority === c.priority && item.extras?.note === c.extras?.note && item.pidStatus === c.pidStatus &&
                item.dbcFileKey === c.dbcFileKey)) &&
            item.company?.id === c.company?.id);
        }
      })

      //if not the same source but with same year and group found in list, mean the paired record is already there, let skip
      if (matchedModelList.find(i => i.source !== current.source && i.yearOfManufacture === current.yearOfManufacture && i.groupId === current.groupId)) {
        return acc;
      }

      let matchedModel = matchedModelList[0];

      let clonedObj = { ...current };
      //check for detail inside to see if they really match
      if (matchedModel) {
        if (currentGroupModel.some(m => {
          return m.attachmentIds !== matchedModel.groupModelDetail[m.source]?.attachmentIds ||
            m.pidStatus !== matchedModel.groupModelDetail[m.source]?.pidStatus ||
            m.priority !== matchedModel.groupModelDetail[m.source]?.priority ||
            m.extras?.note !== matchedModel.groupModelDetail[m.source]?.note ||
            m.connectionPoint !== matchedModel.groupModelDetail[m.source]?.connectionPoint ||
            m.dbcFileKey !== matchedModel.groupModelDetail[m.source]?.dbcFileKey;
        })) {
          matchedModel = undefined;
        }
      }

      if (!matchedModel) {
        clonedObj.yearList = [clonedObj.yearOfManufacture];
        clonedObj.yearListString = clonedObj.yearOfManufacture + "";
        clonedObj.groupModel = currentGroupModel;
        clonedObj.fullGroupModels = [...clonedObj.groupModel];
        clonedObj.groupModelDetail = clonedObj.groupModel.reduce((acc, item) => {
          acc[item.source] = {
            pidStatus: item.pidStatus,
            attachmentIds: item.attachmentIds,
            dbcFileKey: item.dbcFileKey,
            priority: item.priority,
            note: item.extras?.note,
            connectionPoint: item.connectionPoint
          };
          return acc;
        }, {});
        delete clonedObj.vins;
        acc.push(clonedObj);
      }
      else if (current.yearOfManufacture) {
        matchedModel.yearList.push(current.yearOfManufacture);
        matchedModel.yearListString = matchedModel.yearListString + "," + current.yearOfManufacture;
        matchedModel.fullGroupModels = [...matchedModel.fullGroupModels, ...currentGroupModel];
      }

      return acc;
    }, []);
    
    return {
      isFetching,
      vehicleCompatibility: uniqueList,
      rawVehicleCompatibility : vehicleCompatibility
    };

  }, [vehicleCompatibility, isFetching]);
}

export const getVehicleCompatibilityQueryDetail = (getState) => {
  const selectUser = UserApi.endpoints.getUserInfo.select({ userId: getState().user?.current?.id });
  const currentUser = selectUser(getState()).data;

  const userKey = getState().user?.current?.auth?.key;
  const isAdminOrSiteAdmin = checkIsAdminOrSiteAdmin(currentUser);
  const payload = (({ make, model, year }) => ({ make, model, year }))(getState().vehicleModelFilter.searchFilter ?? {});

  return {currentUser, userKey, isAdminOrSiteAdmin, payload}
}